import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { t } from '../../lib/strings.js';

/**
 * RIM-186 - a folha que sobe de baixo, em um lugar só.
 *
 * Era o terceiro lugar do produto a desenhar a mesma coisa: o menu "Mais" da
 * navegação, os detalhes do cliente e agora a troca de etapa. Três cópias do
 * mesmo overlay divergem na primeira correção que alguém faz em uma delas.
 *
 * Sobe de baixo porque é de baixo que o dedo vem — no telefone o que abre uma
 * folha está sempre na metade inferior da tela.
 *
 * **Por que folha, e não um menu flutuante.** A troca de etapa era um dropdown
 * que abria para cima e ficava cortado pelo cartão: as primeiras etapas
 * sumiam acima da borda. Um menu que esconde parte das opções é um caminho
 * quebrado — e não havia sinal de que faltava coisa ali.
 */
export function BottomSheet({
  title,
  children,
  onClose,
  /** `85` deixa uma faixa do que está atrás à mostra; `auto` encolhe ao conteúdo. */
  height = 'auto',
  panelClassName,
  /**
   * Até onde a folha vale, em CSS.
   *
   * A navegação some em 768px, então a folha do menu "Mais" também — para ela um
   * breakpoint resolve. `'nenhum'` é para quem já decide em JavaScript se a
   * folha existe: a coluna de contexto do cliente aparece conforme a largura
   * REAL da conversa, que não acompanha a da janela, e um `lg:hidden` a
   * esconderia justamente nas larguras em que ela precisa existir.
   */
  ate = 'md',
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  height?: 'auto' | 'tall';
  panelClassName?: string;
  ate?: 'md' | 'lg' | 'nenhum';
}): ReactNode {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const focusable = () =>
      Array.from(
        panel?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex="0"]',
        ) ?? [],
      ).filter((element) => element.getClientRects().length > 0);
    (panel?.querySelector<HTMLElement>('input[type="search"]') ?? focusable()[0] ?? panel)?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current();
      }
      if (event.key !== 'Tab') return;
      const entries = focusable();
      const first = entries[0];
      const last = entries.at(-1);
      if (!first) {
        event.preventDefault();
        panel?.focus();
        return;
      }
      if (
        event.shiftKey &&
        (document.activeElement === first || !panel?.contains(document.activeElement))
      ) {
        event.preventDefault();
        last?.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || !panel?.contains(document.activeElement))
      ) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (previous?.isConnected) previous.focus();
    };
  }, []);

  /*
    PORTAL, E NAO E DETALHE. `position: fixed` se resolve contra a janela — exceto
    quando algum ancestral tem `transform`, `filter` ou `contain`; ai o ancestral
    vira o bloco de contencao e o "fixed" passa a ser relativo a ELE.

    Foi o que aconteceu: o cartao de oportunidade usa `animate-rise`, que anima
    transform, e a folha apareceu desenhada dentro do cartao em vez de subir do
    rodape da tela. Levar para o `body` tira a folha do alcance de qualquer
    transform que exista ou venha a existir acima dela.
  */
  return createPortal(
    <div
      className={cn(
        'app-sheet-root fixed inset-0 z-40 flex flex-col justify-end',
        ate === 'md' && 'md:hidden',
        ate === 'lg' && 'lg:hidden',
      )}
    >
      <button
        type="button"
        aria-label={t.common.close}
        onClick={onClose}
        className="absolute inset-0 bg-ink/25"
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'animate-rise relative flex min-h-0 flex-col rounded-t-card border-t border-line bg-surface',
          height === 'tall' ? 'max-h-[85%]' : 'max-h-[70%]',
          panelClassName,
        )}
      >
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
          <span className="text-[15px] font-semibold text-ink">{title}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label={t.common.close}
            className="flex size-11 shrink-0 items-center justify-center rounded-control text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink-soft"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rimati-scroll">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
